import { motion } from "framer-motion";
import { 
  MonitorPlay, 
  Users, 
  Brain, 
  Dumbbell, 
  ShieldCheck 
} from "lucide-react";

const highlights = [
  {
    icon: MonitorPlay,
    title: "Modern Classrooms",
    description: "Smart boards, projectors, and digital learning tools for interactive education",
    color: "bg-blue-50",
    iconColor: "text-blue-600",
  },
  {
    icon: Users,
    title: "Qualified Teachers",
    description: "Experienced and certified educators dedicated to student success",
    color: "bg-amber-50",
    iconColor: "text-amber-600",
  },
  {
    icon: Brain,
    title: "Smart Learning",
    description: "Activity-based learning approach with focus on practical knowledge",
    color: "bg-green-50",
    iconColor: "text-green-600",
  },
  {
    icon: Dumbbell,
    title: "Sports Facilities",
    description: "Well-equipped sports ground and indoor games for physical development",
    color: "bg-red-50",
    iconColor: "text-red-600",
  },
  {
    icon: ShieldCheck,
    title: "Safe Campus",
    description: "24/7 CCTV surveillance and trained security staff for student safety",
    color: "bg-purple-50",
    iconColor: "text-purple-600",
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  show: { opacity: 1, y: 0, transition: { duration: 0.5 } },
};

export const HighlightsSection = () => {
  return (
    <section className="py-16 md:py-24 bg-slate-50" data-testid="highlights-section">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-16"
        >
          <span className="text-secondary font-medium text-sm uppercase tracking-wider">
            Why Choose Us
          </span>
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-gray-900 mt-2 mb-4">
            What Makes Us Special
          </h2>
          <div className="section-divider mx-auto"></div>
          <p className="text-gray-600 mt-4 max-w-2xl mx-auto">
            ID4U School offers a nurturing environment with world-class facilities 
            and a dedicated team committed to your child's holistic development.
          </p>
        </motion.div>

        {/* Highlights Grid */}
        <motion.div
          variants={containerVariants}
          initial="hidden"
          whileInView="show"
          viewport={{ once: true }}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6"
        >
          {highlights.map((item, index) => (
            <motion.div
              key={item.title}
              variants={itemVariants}
              className="bg-white rounded-2xl p-6 shadow-sm border border-slate-100 hover:shadow-xl hover:-translate-y-2 transition-all duration-300 card-hover"
              data-testid={`highlight-${index}`}
            >
              <div className={`w-14 h-14 ${item.color} rounded-xl flex items-center justify-center mb-4`}>
                <item.icon className={`w-7 h-7 ${item.iconColor}`} />
              </div>
              <h3 className="font-heading text-lg font-semibold text-gray-900 mb-2">
                {item.title}
              </h3>
              <p className="text-sm text-gray-600 leading-relaxed">
                {item.description}
              </p>
            </motion.div>
          ))}
        </motion.div>
      </div>
    </section>
  );
};
